import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextExpenseReport } from './ExpenseReport';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { getError } from '../../../../languages/translations/response';
import { ExpenseReportDA } from '../../../../services/admin/expense.report.service';
import { Error } from '../../../../models/error.model';
import { CompanyExpenseReports } from '../../../../models/company.expense.reports.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ExpenseReport } from '../../../../models/expense.report.model';
import { ExpenseReportGroup } from '../../../../models/expense.report.group.model';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { accumulatedAmount, isVatSubject } from '../../../../scripts/financial.calculation.script';
import { nameGroup } from '../../../../libraries/group.name.library';
import { colorStatusExpenseReport, colorStatusPayment } from '../../../../scripts/badge.color.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { paymentStatusValue } from '../../../../libraries/payment.library';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppPagination from '../../../../components/element/Pagination';
import AppSortTable from '../../../../components/element/SortTable';
import AppPreloader from '../../../../components/Preloader';
import AppNone from '../../../../components/element/None';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';
import AppModalExpenseReportExport from '../../../../components/modal/ModalExpenseReportExport';
import { modalShow } from '../../../../utils/modal.util';

export interface ExpenseReportListPageProps {};

let errorResponse: Error, reportsResponse: CompanyExpenseReports;

const ExpenseReportListPage: React.FunctionComponent<ExpenseReportListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, contactsForCompany} = UseOutletContextExpenseReport()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState({filter: false, group: false})
  const [filterState, setFilterState] = useState({interval: '', start_date: '', end_date: '', contact: '', contact_label: '', status: '', payment: '', sort_field: '', sort_mode: '', group: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [reports, setReports] = useState<ExpenseReport[] | null>(null)
  const [reportGroups, setReportGroups] = useState<ExpenseReportGroup[] | null>(null)
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);
  // Nombre de colonnes AVANT le bloc montants (TVA/total). La colonne TVA fait partie du bloc montants.
  const COLS_BEFORE_AMOUNTS = 6;

  const loadReports = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    try {
      const response = await ExpenseReportDA.getExpenseReports(id_company, f.interval, f.start_date, f.end_date, f.contact, f.status, f.payment, f.sort_field, f.sort_mode, f.group, page, pagination.limit, f.search)
      if (response.status === 200) {
        reportsResponse = response.data

        const filterData = {
          company_id: id_company,
          interval: f.interval,
          start_date: f.start_date,
          end_date: f.end_date,
          contact: f.contact,
          contact_label: f.contact_label,
          status: f.status,
          payment: f.payment,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          group: f.group,
          search: f.search
        }
        localStorage.setItem('filter_expense_reports', JSON.stringify(filterData))

        const groupBy = filterData.group
        const items = (reportsResponse.items ?? reportsResponse.expense_reports ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))
        const groups = (reportsResponse.groups ?? []).map((group: any) => ({ ...group, items: (group.items ?? group.expense_reports ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) })) }))

        localStorage.setItem('nav_expense_reports', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        if (!groupBy || groupBy.length === 0) {
          setReportGroups([])
          setReports(items)

          const totalPages = reportsResponse.pagination?.pages || 1
          if (page > totalPages && totalPages >= 1) {
            setUrlPage(totalPages)
            loadReports(id_company, totalPages)
            return
          }
          let pagesOfPagination: number[] = []
          for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
          setPagination({...pagination, current: reportsResponse.pagination?.page || 1, total: reportsResponse.pagination?.total || 0, pages: pagesOfPagination})
          setUrlPage(reportsResponse.pagination?.page || 1)
        } else {
          setReports([])
          setReportGroups(groups)
          setPagination({current: 1, limit: 20, total: 0, pages: [1]})
          setUrlPage(1)
        }

        setButton({filter: Boolean(filterData.interval || filterData.start_date || filterData.end_date || filterData.contact || filterData.status || filterData.payment), group: Boolean(groupBy)})
      } else {
        errorResponse = response.data
        setReports([])
        setReportGroups([])

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      setReports([])
      setReportGroups([])
      errorWebHandler(lang, error)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadReports(companyForUser.id, current)
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadReports(companyForUser.id, 1)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setFilter(prev => ({ ...prev, search: value }))
    if (companyForUser) {
      setTimeout(() => {
        loadReports(companyForUser.id, 1)
      }, 400)
    }
  }

  useEffect( () => {
    let filter_reports: any = (localStorage.getItem('filter_expense_reports')) ? JSON.parse(localStorage.getItem('filter_expense_reports')!) : null

    if (companyForUser) {
      const isMatch = filter_reports && filter_reports.company_id === companyForUser.id
      setFilter({
        interval: isMatch ? filter_reports.interval : '',
        start_date: isMatch ? filter_reports.start_date : '',
        end_date: isMatch ? filter_reports.end_date : '',
        contact: isMatch ? filter_reports.contact : '',
        contact_label: isMatch ? (filter_reports.contact_label ?? '') : '',
        status: isMatch ? filter_reports.status : '',
        payment: isMatch ? filter_reports.payment : '',
        sort_field: isMatch ? filter_reports.sort_field : '',
        sort_mode: isMatch ? filter_reports.sort_mode : '',
        group: isMatch ? filter_reports.group : '',
        search: isMatch ? (filter_reports.search ?? '') : ''
      })

      setReports(null)
      setReportGroups(null)
      setButton({filter: false, group: false})
      loadReports(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.expenseReports, branch: lang.labels.listExpenseReports}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && reports && reportGroups
        ?
        <div className="card card-flush">
          <div className="card-header pt-5">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative my-1">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control form-control-solid w-250px ps-14" type="text" placeholder={lang.labels.search} value={filter.search} onChange={handleSearchChange} />
              </div>
            </div>
            <div className="card-toolbar">
              <button className="btn btn-primary me-2" type="button" onClick={() => modalShow('modal-expense-report-export')}>
                {lang.labels.export}
              </button>
              <Link to="/main/purchase/expense-report/create" className="btn btn-primary">
                <span className="svg-icon svg-icon-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="black" />
                    <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="black" />
                  </svg>
                </span>
                {lang.labels.createExpenseReport}
              </Link>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("expense_report_number")}>
                          <span className="me-1">{lang.labels.expenseReportNumber}</span>
                          <AppSortTable attribute={"expense_report_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("employee")}>
                          <span className="me-1">{lang.labels.employee}</span>
                          <AppSortTable attribute={"employee"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("payment_status")}>
                          <span className="me-1">{lang.labels.payment}</span>
                          <AppSortTable attribute={"payment_status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center">
                        <span>{lang.labels.vouchers}</span>
                      </th>
                    </tr>
                  </thead>
                  { reports.length > 0 || reportGroups.length > 0
                    ?
                    <>
                      { button.group
                        ?
                        <>
                          { reportGroups.map (( (item, index) => { return (
                            <tbody key={index} className="text-secondary fw-bold">
                              <tr>
                                <td colSpan={8 + (vatSubject ? 1 : 0)} className="text-uppercase fw-bolder fs-7">
                                  <div className="d-flex align-items-center">
                                    <span className="svg-icon svg-icon-3 svg-icon-gray-900 me-5">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M22 12C22 12.9 21.9 13.8 21.7 14.6L5 4.89999C6.8 3.09999 9.3 2 12 2C17.5 2 22 6.5 22 12Z" fill="black"/>
                                        <path opacity="0.3" d="M3.7 17.5C2.6 15.9 2 14 2 12C2 9.20003 3.1 6.70002 5 4.90002L9.3 7.40002V14.2L3.7 17.5ZM17.2 12L5 19.1C6.8 20.9 9.3 22 12 22C16.6 22 20.5 18.8 21.7 14.6L17.2 12Z" fill="black"/>
                                      </svg>
                                    </span>
                                    <span className="me-3">{lang.labels.group} {index + 1}:</span>
                                    {nameGroup(lang, filter.group, item.group_detail)}
                                  </div>
                                </td>
                              </tr>
                              { (item.items ?? item.expense_reports ?? []).map (( (sub_item: ExpenseReport, sub_index: number) => { return (
                                <tr key={sub_index} className="row-dashed">
                                  <td className="text-nowrap"><AppNone condition={true} display={dateFormat(lang.code, sub_item.created_date)}></AppNone></td>
                                  <td>
                                    { sub_item.status === "draft" || sub_item.status === "rejected"
                                      ?
                                      <Link to={"/main/purchase/expense-report/update/" + sub_item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.expense_report_number}</Link>
                                      :
                                      <Link to={"/main/purchase/expense-report/view/" + sub_item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.expense_report_number}</Link>
                                    }
                                  </td>
                                  <td><AppNone condition={true} display={sub_item.concerning ?? ''}></AppNone></td>
                                  <td><AppNone condition={true} display={formatContact(sub_item.employee) || '—'}></AppNone></td>
                                  <td><span className={`badge badge-light-${colorStatusExpenseReport(sub_item.status)} fs-7`}>{statusDocumentValue(lang, sub_item.status)}</span></td>
                                  <td>
                                    { sub_item.status === 'validated' && sub_item.payment_status
                                      ? <span className={`badge badge-light-${colorStatusPayment(sub_item.payment_status)} fs-7`}>{paymentStatusValue(lang, normalizeStatus(sub_item.payment_status))}</span>
                                      : <span>—</span>
                                    }
                                  </td>
                                  {vatSubject && <td className="text-end text-nowrap">{sub_item.currency?.code ?? ''} {formatMoney(sub_item.amount?.tax_total ?? 0)}</td>}
                                  <td className="text-end text-nowrap">
                                    <AppNone condition={true} display={`${sub_item.currency?.code ?? ''} ${formatMoney(sub_item.amount?.total ?? 0)}`}></AppNone>
                                  </td>
                                  <td className="text-center"><ButtonVouchers vouchers={sub_item.vouchers} /></td>
                                </tr>
                              )}))}
                              <tr className={`fw-bolder fs-7 ${index < reportGroups.length - 1 && "row-dashed"}`}>
                                <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                                {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.items ?? item.expense_reports ?? []).tax)}</td>}
                                <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.items ?? item.expense_reports ?? []).total)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          )}))}
                        </>
                        :
                        <>
                          <tbody className="text-secondary fw-bold">
                            { reports.map (( (item, index) => { return (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap"><AppNone condition={true} display={dateFormat(lang.code, item.created_date)}></AppNone></td>
                                <td>
                                  { item.status === "draft" || item.status === "rejected"
                                    ?
                                    <Link to={"/main/purchase/expense-report/update/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.expense_report_number}</Link>
                                    :
                                    <Link to={"/main/purchase/expense-report/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.expense_report_number}</Link>
                                  }
                                </td>
                                <td><AppNone condition={true} display={item.concerning ?? ''}></AppNone></td>
                                <td><AppNone condition={true} display={formatContact(item.employee) || '—'}></AppNone></td>
                                <td><span className={`badge badge-light-${colorStatusExpenseReport(item.status)} fs-7`}>{statusDocumentValue(lang, item.status)}</span></td>
                                <td>
                                  { item.status === 'validated' && item.payment_status
                                    ? <span className={`badge badge-light-${colorStatusPayment(item.payment_status)} fs-7`}>{paymentStatusValue(lang, normalizeStatus(item.payment_status))}</span>
                                    : <span>—</span>
                                  }
                                </td>
                                {vatSubject && <td className="text-end text-nowrap">{item.currency?.code ?? ''} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                                <td className="text-end text-nowrap">
                                  <AppNone condition={true} display={`${item.currency?.code ?? ''} ${formatMoney(item.amount?.total ?? 0)}`}></AppNone>
                                </td>
                                <td className="text-center"><ButtonVouchers vouchers={item.vouchers} /></td>
                              </tr>
                            )}))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bolder fs-7">
                              <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                              {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(reports).tax)}</td>}
                              <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(reports).total)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </>
                      }
                    </>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              {!button.group && <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>}
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalExpenseReportExport companyForUser={companyForUser} grouped={{state: button.group, desc: filterState.group}} expenseReports={reports} expenseReportGroups={reportGroups} filter={filterState} vatSubject={vatSubject}></AppModalExpenseReportExport>
    </div>
  )
}

export default ExpenseReportListPage;
